import React, { useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CONTACT, RDV, SERVICES } from "../config/routes";

import "./styles/PrivacyPolicy.css";

import privacyPolicyBackgroundPng from "../images/png/background/privacy_policy_background.png";
import privacyPolicyBackgroundAvif from "../images/avif/background/privacy_policy_background.avif";
import privacyPolicyBackgroundWebp from "../images/webp/background/privacy_policy_background.webp";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();
    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".privacy2026");
    if (!mainContainer) return;
    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const metaTitle = t("privacyPolicy.metaTitle");
  const metaDesc = t("privacyPolicy.metaContent");

  const sections = useMemo(
    () => [
      { id: "intro", label: t("privacyPolicy.dreamerAgencyPrivacy") },
      { id: "collecte", label: t("privacyPolicy.pointOne") },
      { id: "utilisation", label: t("privacyPolicy.pointTwo") },
      { id: "cookies", label: t("privacyPolicy.pointThree") },
      { id: "partage", label: t("privacyPolicy.pointFour") },
      { id: "securite", label: t("privacyPolicy.pointFive") },
      { id: "conservation", label: t("privacyPolicy.pointSix") },
      { id: "droits", label: t("privacyPolicy.pointSeven") },
    ],
    [t]
  );

  return (
    <main className={`privacy2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href="https://dreameragency.fr/privacy-policy" />

        <meta
          property="og:title"
          content="Politique de Confidentialité de Dreamer Agency - Protéger vos données est notre priorité"
        />
        <meta
          property="og:description"
          content="Découvrez comment Dreamer Agency recueille, utilise et protège vos informations personnelles."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dreameragency.fr/privacy-policy" />
        <meta property="og:image" content="https://dreameragency.fr/media/OG/privacy_policy_background.png" />
      </Helmet>

      <header className="privacy2026-hero">
        <div
          className="privacy2026-heroBg"
          style={{ backgroundImage: `url(${privacyPolicyBackgroundPng})` }}
          aria-hidden="true"
        />
        <div className="privacy2026-heroOverlay" aria-hidden="true" />

        <div className="privacy2026-container privacy2026-heroInner">
          <nav className="privacy2026-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="crumb">Accueil</Link>
            <span className="sep">/</span>
            <span className="crumbCurrent" aria-current="page">{t("privacyPolicy.headerTitle")}</span>
          </nav>

          <h1 className="privacy2026-h1">{t("privacyPolicy.headerTitle")}</h1>
          <p className="privacy2026-lead">{t("privacyPolicy.headerParagraph")}</p>

          <div className="privacy2026-heroActions">
            <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
            <Link to={CONTACT} className="btn btn-secondary">Contact</Link>
            <Link to={SERVICES} className="btn btn-ghost">Services</Link>
          </div>
        </div>
      </header>

      <section className="privacy2026-section">
        <div className="privacy2026-container privacy2026-grid">
          <aside className="privacy2026-side" aria-label="Sommaire">
            <div className="sideCard">
              <p className="sideTitle">Sommaire</p>
              <div className="toc">
                {sections.map((s) => (
                  <a key={s.id} href={`#${s.id}`} className="tocLink">
                    {s.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="sideCard sideCardSoft">
              <p className="sideTitle">Besoin d’aide ?</p>
              <p className="privacy2026-muted">
                Pour toute question sur vos données, contactez-nous ou réservez un appel.
              </p>
              <Link to={CONTACT} className="btn btn-secondary btn-block">Contact</Link>
              <Link to={RDV} className="btn btn-primary btn-block">Réserver un appel</Link>
            </div>
          </aside>

          <article className="privacy2026-content">
            <div className="privacy2026-card" id="intro">
              <h2 className="privacy2026-h2">{t("privacyPolicy.dreamerAgencyPrivacy")}</h2>
              <p className="privacy2026-muted">{t("privacyPolicy.updateDate")}</p>
              <p className="privacy2026-text">{t("privacyPolicy.headerParagraph")}</p>
            </div>

            <div className="privacy2026-card" id="collecte">
              <h3 className="privacy2026-h3">{t("privacyPolicy.pointOne")}</h3>
              <p className="privacy2026-text">{t("privacyPolicy.pointOneParagraph")}</p>
              <ul className="privacy2026-list">
                <li>{t("privacyPolicy.pointOneListOne")}</li>
                <li>{t("privacyPolicy.pointOneListTwo")}</li>
                <li>{t("privacyPolicy.pointOneListThree")}</li>
                <li>{t("privacyPolicy.pointOneListFour")}</li>
              </ul>
            </div>

            <div className="privacy2026-card" id="utilisation">
              <h3 className="privacy2026-h3">{t("privacyPolicy.pointTwo")}</h3>
              <ul className="privacy2026-list">
                <li>{t("privacyPolicy.pointTwoParagraph")}</li>
                <li>{t("privacyPolicy.pointTwoListOne")}</li>
                <li>{t("privacyPolicy.pointTwoListTwo")}</li>
                <li>{t("privacyPolicy.pointTwoListThree")}</li>
                <li>{t("privacyPolicy.pointTwoListFour")}</li>
              </ul>
            </div>

            <div className="privacy2026-card" id="cookies">
              <h3 className="privacy2026-h3">{t("privacyPolicy.pointThree")}</h3>
              <p className="privacy2026-text">{t("privacyPolicy.pointThreeParagraph")}</p>
            </div>

            <div className="privacy2026-card" id="partage">
              <h3 className="privacy2026-h3">{t("privacyPolicy.pointFour")}</h3>
              <p className="privacy2026-text">{t("privacyPolicy.pointFourParagraph")}</p>
            </div>

            <div className="privacy2026-card" id="securite">
              <h3 className="privacy2026-h3">{t("privacyPolicy.pointFive")}</h3>
              <p className="privacy2026-text">{t("privacyPolicy.pointFiveParagraph")}</p>
            </div>

            <div className="privacy2026-card" id="conservation">
              <h3 className="privacy2026-h3">{t("privacyPolicy.pointSix")}</h3>
              <p className="privacy2026-text">{t("privacyPolicy.pointSixParagraph")}</p>
            </div>

            <div className="privacy2026-card" id="droits">
              <h3 className="privacy2026-h3">{t("privacyPolicy.pointSeven")}</h3>
              <p className="privacy2026-text">{t("privacyPolicy.pointSevenParagraph")}</p>
              <a className="privacy2026-mail" href={`mailto:${t("privacyPolicy.ourEmail")}`}>
                {t("privacyPolicy.ourEmail")}
              </a>
            </div>

            <div className="privacy2026-cta">
              <div>
                <h2 className="privacy2026-h2">On en parle ?</h2>
                <p className="privacy2026-muted">
                  Si vous avez une question sur vos données ou un projet à lancer, le plus simple est de réserver un appel.
                </p>
              </div>
              <div className="privacy2026-ctaActions">
                <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
                <Link to={CONTACT} className="btn btn-ghost">Contact</Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
